/**
 * Lightweight localStorage hint for boot skeletons.
 * Redux persist is encrypted + session-scoped, so FCP shells cannot read isOnboarded from the store.
 * When false/missing → first-time users on `/` get the onboarding skeleton instead of dashboard.
 */

const STORAGE_KEY_PREFIX = "ecomzenith_etsy_onboarded";

function shopScope(): string {
  if (typeof window === "undefined") return "default";
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("shop") || "default";
  } catch {
    return "default";
  }
}

function storageKey(): string {
  return `${STORAGE_KEY_PREFIX}:${shopScope()}`;
}

/** True when this shop previously completed onboarding (survives refresh / new iframe session). */
export function hasCompletedOnboardingHint(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(storageKey()) === "1";
  } catch {
    return false;
  }
}

/** Keep boot skeleton in sync when Redux onboarding state changes. */
export function setOnboardingCompletedHint(completed: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const key = storageKey();
    if (completed) {
      window.localStorage.setItem(key, "1");
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // private mode / quota — boot falls back to onboarding skeleton
  }
}

function normalizeAppPath(pathname: string): string {
  return pathname.replace(/^\/etsy/, "") || "/";
}

/** Home entry (`/` / `/dashboard`) before redirect: onboarding unless hint says completed. */
export function shouldShowOnboardingBootShell(pathname?: string): boolean {
  const path = normalizeAppPath(
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/"),
  );

  if (path === "/onboarding" || path.startsWith("/onboarding/")) return true;

  if (path === "/" || path === "/dashboard" || path === "/null") {
    return !hasCompletedOnboardingHint();
  }

  return false;
}

/** Home entry after onboarding is done — dashboard boot skeleton. */
export function shouldShowDashboardBootShell(pathname?: string): boolean {
  const path = normalizeAppPath(
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/"),
  );

  if (path === "/" || path === "/dashboard" || path === "/null") {
    return hasCompletedOnboardingHint();
  }

  return false;
}
